import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminBulkImport, getAdminToken, adminExport } from '../../api/client';

const SAMPLE = `[
  {
    "name": "Example Tool",
    "slug": "example-tool",
    "website_url": "https://example.com",
    "resource_type": "tool",
    "primary_category": "Text & Writing",
    "tags": ["writing", "example"],
    "badges": ["editors_pick"]
  }
]`;

export default function AdminImportExport() {
  const [json, setJson] = useState(SAMPLE);
  const [upsert, setUpsert] = useState(true);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exportType, setExportType] = useState('');
  const nav = useNavigate();

  if (!getAdminToken()) {
    nav('/admin/login');
    return null;
  }

  const runImport = async () => {
    setBusy(true); setResult(null);
    try {
      const parsed = JSON.parse(json);
      const items = Array.isArray(parsed) ? parsed : parsed.items;
      if (!Array.isArray(items)) throw new Error('Payload must be an array or object with an items array');
      const res = await adminBulkImport(items, upsert);
      setResult({ ok: true, ...res });
    } catch (err) {
      setResult({ ok: false, error: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(Boolean);
      const [headerLine, ...rows] = lines;
      const headers = headerLine.split(',').map((h) => h.trim());
      const items = rows.map((row) => {
        // Naive CSV parse — good enough for our export format; production would use PapaParse
        const cells = row.split(',');
        const obj = {};
        headers.forEach((h, i) => {
          const v = (cells[i] || '').trim();
          if (['tags', 'badges', 'additional_categories'].includes(h)) obj[h] = v ? v.split('|') : [];
          else if (['featured', 'sponsored', 'verified'].includes(h)) obj[h] = v.toLowerCase() === 'true';
          else obj[h] = v || null;
        });
        return obj;
      });
      const res = await adminBulkImport(items, upsert);
      setResult({ ok: true, ...res, from_csv: true, parsed: items.length });
    } catch (err) {
      setResult({ ok: false, error: err.message });
    } finally {
      setBusy(false);
    }
  };

  const download = async (format) => {
    setBusy(true); setResult(null);
    try {
      const data = await adminExport(exportType || undefined, format);
      const blob = format === 'csv'
        ? new Blob([data], { type: 'text/csv' })
        : new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-framework-export${exportType ? '-' + exportType : ''}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      setResult({ ok: true, exported: format });
    } catch (err) {
      setResult({ ok: false, error: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-container admin-page">
      <div className="crumbs">
        <Link to="/admin/tools">Resources</Link>
        <span>/</span>
        <span>Import / Export</span>
      </div>
      <h1 className="page-title">Import / Export</h1>

      <section className="admin-block" data-testid="admin-import-block">
        <h3>Import</h3>
        <p className="muted">Bulk-insert or upsert resources. Existing resources (matched by slug) are updated non-destructively — new categories merge into <code>additional_categories</code>, tags/badges dedupe-merge, and featured/verified flags are preserved unless explicitly overridden.</p>

        <div className="field">
          <span>Paste JSON array (or {`{items: [...] }`})</span>
          <textarea
            rows={12}
            value={json}
            onChange={(e) => setJson(e.target.value)}
            data-testid="import-json-textarea"
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          />
        </div>
        <div className="checkbox-row">
          <label><input type="checkbox" checked={upsert} onChange={(e) => setUpsert(e.target.checked)} /> Upsert (update existing by slug)</label>
        </div>
        <div className="form-actions">
          <label className="pill-btn ghost" style={{ cursor: 'pointer' }}>
            Upload CSV
            <input type="file" accept=".csv,text/csv" onChange={handleCsvUpload} style={{ display: 'none' }} data-testid="import-csv-input" />
          </label>
          <button className="pill-btn primary" type="button" onClick={runImport} disabled={busy} data-testid="run-import-btn">
            {busy ? '…' : 'Run import'}
          </button>
        </div>
      </section>

      <section className="admin-block" style={{ marginTop: 24 }} data-testid="admin-export-block">
        <h3>Export</h3>
        <div className="field-row">
          <label className="field">
            <span>Filter by resource type (blank = all)</span>
            <input value={exportType} onChange={(e) => setExportType(e.target.value)} placeholder="e.g. creator, tool, company" data-testid="export-type-input" />
          </label>
        </div>
        <div className="form-actions">
          <button className="pill-btn" type="button" onClick={() => download('json')} disabled={busy} data-testid="export-json-btn">Export JSON</button>
          <button className="pill-btn" type="button" onClick={() => download('csv')} disabled={busy} data-testid="export-csv-btn">Export CSV</button>
        </div>
      </section>

      {result && (
        <section className={`admin-block ${result.ok ? '' : 'error'}`} style={{ marginTop: 24 }} data-testid="import-result">
          <h3>Result</h3>
          <pre style={{ background: 'var(--surface)', padding: 12, borderRadius: 8, overflowX: 'auto', fontSize: 12 }}>{JSON.stringify(result, null, 2)}</pre>
        </section>
      )}
    </div>
  );
}
